import { apiErrorMessage } from '@agos/api-client'
import { useQuery } from '@tanstack/react-query'
import { useGetEmployeesWaiters } from '@agos/api-client/hr'
import { postFiskalReceipt, useGetFiskalRegisters } from '@agos/api-client/fiskal'
import {
  useGetOrdersOrderIdEditLogs,
  useGetOrdersOrderIdStatusLogs,
  useGetSalesChannels,
  usePatchOrdersOrderId,
} from '@agos/api-client/orders'
import type { OrderListItem, OrderStatusLog } from '@agos/api-client/model'
import {
  alreadyPrinted,
  alreadyPrintedNote,
  EmployeeAuthModal,
  fetchOrderDocuments,
  FISKAL_STORNO,
  fiskalDocumentEvents,
  fiskalDocumentsOf,
  formatCents,
  formatMoney,
  lineTotalCents,
  lineVatLabel,
  ordersLinesShareOneRate,
  parseMoneyToCents,
  printVirtualReceipt,
  registerName,
  sharedVatLabel,
  SHIPPED_REGISTER_NAMES,
  T,
  tt,
  useAuth,
  virtualReceiptOf,
  type FiskalDocumentEvent,
} from '@agos/shared'
import { cn, toast } from '@agos/ui'
import {
  Ban,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Edit2,
  Globe,
  History,
  MapPin,
  Phone,
  Printer,
  Receipt,
  RotateCcw,
  ShoppingBag,
  Star,
  Store,
  Truck,
} from 'lucide-react'
import { useMemo, useState } from 'react'

import {
  ORDER_STATUS_LABELS,
  ORDER_TYPE_LABELS,
  ARCHIVE_PAYMENT_CHOICES,
  paymentChoiceLabel,
  paymentChoiceNote,
  orderStatusLabel,
  paymentMethodLabel,
} from '../order-status'
import { sourceBadgeKind, sourceBadgeLabel, type BadgeChannel } from './source-badge'
import {
  describeEditLogChange,
  editLogFieldLabel,
  editedFlagLabel,
  mergedHistory,
  type EditLogEntry,
  type HistoryRow,
} from './edit-log'
import { receiptLines } from '../restaurant/split-payment'
import { fiskalRowActions } from './fiskal-row-actions'
import { discountSource } from './discount-source'
import { needsWaiterBeforeClosing, waiterOptions } from '../order-builder/waiter-choice'
import {
  ALL_STATUSES,
  PROTECTED_STATUSES,
  availableTransitions,
  canReturnToKitchen,
} from './status-transitions'
import { fiskalMismatch, fiskalMismatchNote } from './fiskal-mismatch'
import { OrderBlockAction } from './OrderBlockAction'

/**
 * One row of the archive, and everything that opens underneath it.
 *
 * The row is the whole screen's reason to exist: it is where a delivered order
 * gets corrected. Every control below the fold either moves money or moves the
 * record of who moved it, so each is behind its own permission and most are
 * behind a step-up prompt.
 */

const TYPE_ICONS: Record<string, typeof Store> = {
  delivery: Truck,
  restaurant: Store,
  bar: ShoppingBag,
}

/**
 * The pill palette. Kept as its own map rather than reused from
 * `orderStatusColor` because the archive draws bordered pills and `StatusBadge`
 * draws filled ones — same vocabulary, different shape.
 */
const STATUS_PILL_CLASSES: Record<string, string> = {
  new: 'bg-blue-500/10 text-blue-500 border-blue-500/20',
  preparing: 'bg-amber-500/10 text-amber-500 border-amber-500/20',
  ready: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  on_the_way: 'bg-violet-500/10 text-violet-500 border-violet-500/20',
  at_address: 'bg-orange-500/10 text-orange-500 border-orange-500/20',
  delivered: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20',
  quality_check: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20',
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20',
}

const TRANSITION_BUTTON_CLASSES: Record<string, string> = {
  cancelled: 'bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20',
  quality_check: 'bg-cyan-500/10 text-cyan-500 border-cyan-500/20 hover:bg-cyan-500/20',
  delivered: 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20',
}

const TRANSITION_ICONS: Record<string, typeof Ban> = {
  cancelled: Ban,
  quality_check: Star,
  delivered: CheckCircle2,
}

const SELECT_CLASSES =
  'flex-1 bg-background border border-amber-500/30 rounded-xl px-3 py-2 text-xs font-semibold text-foreground focus:outline-none focus:ring-2 focus:ring-amber-500/30'

const ADMIN_BUTTON_CLASSES =
  'flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold bg-amber-500/20 text-amber-500 border border-amber-500/30 hover:bg-amber-500/30 transition-all whitespace-nowrap'

/** The lock the row prints next to anything that will ask for a password. */
function StepUpHint() {
  return (
    <span className="text-[9px] font-medium opacity-60 flex items-center gap-0.5">
      🔒 <T>overenie</T>
    </span>
  )
}

function formatDateTime(value: string | undefined): string {
  return value ? new Date(value).toLocaleString('sk-SK') : '-'
}

export interface OrderRowHandlers {
  onStatusRequest: (order: OrderListItem, newStatus: string, forceAuth?: boolean) => void
  onPaymentMethodRequest: (order: OrderListItem, newMethod: string) => void
  onReturnToKitchen: (order: OrderListItem) => void
  /** The register answered; the page decides what the manager is told. */
  onStornoSuccess: (response: Record<string, unknown> | undefined) => void
  onRefresh: () => void
}

export function OrderRow({
  order,
  onStatusRequest,
  onPaymentMethodRequest,
  onReturnToKitchen,
  onStornoSuccess,
  onRefresh,
}: { order: OrderListItem } & OrderRowHandlers) {
  const [expanded, setExpanded] = useState(false)
  const [stornoTarget, setStornoTarget] = useState<OrderListItem | null>(null)
  const [forcedStatus, setForcedStatus] = useState('')
  const [forcedPaymentMethod, setForcedPaymentMethod] = useState('')
  const { hasPermission, user } = useAuth()
  /* §9.573, FB-866 - `fiskal` covers it as a parent grant, and `fiskal_reconcile` is the key the owner
     asked for so one person beyond the administrators can be given it on its own. */
  const maySeeFiskalMismatch = hasPermission('fiskal') || hasPermission('fiskal_reconcile')
  /* §9.787, owner O140 - the blocklist's right, which every one of its endpoints asks (BACKEND.md §10A.439). */
  const mayBlock = hasPermission('marketing_customers_deactivate')

  // §9.5: the per-line tax is drawn only where an order mixes rates. Computed once for the
  // row rather than per line, so the decision is the order's and not each line's.
  const oneRate = ordersLinesShareOneRate(order.items ?? [])
  const orderVat = sharedVatLabel(order.items ?? [])

  const isMainAdmin = user?.systemRole === 'main_admin'
  const orderId = order.id ?? 0

  // Only fetched once the row is open. An archive page holds fifty rows and
  // almost none of them are ever expanded.
  const { data: editLogs } = useGetOrdersOrderIdEditLogs(orderId, {
    query: { enabled: expanded && orderId > 0, staleTime: 60_000 },
  })

  /**
   * §9.446, FB-763 - the status trail, which had 20 690 rows and no caller.
   *
   * Same gate as the edit log above and for the same reason: an archive page holds fifty rows and
   * almost none are opened. The two are merged into one panel by `mergedHistory`, because the
   * owner's question is *what happened to this order, in order*.
   */
  const { data: statusLogs } = useGetOrdersOrderIdStatusLogs(orderId, {
    query: { enabled: expanded && orderId > 0, staleTime: 60_000 },
  })

  /**
   * §9.570, FB-864 - the third trail, which the owner asked for by name.
   *
   * `fiskal_documents` has recorded every print and every reversal since §10A.191 with the figure the
   * register was told to print, and **nothing could read it back** until §10A.294 - so *História zmien*
   * showed two of the three things that happen to an order.
   *
   * Same gate as the two above: an archive page holds fifty rows and almost none are opened.
   * `customFetch` rather than a generated hook because the endpoint post-dates the exported spec -
   * §9.509, with the path on `typed-fetches.test.ts`'s self-clearing list.
   */
  const { data: fiskalDocuments } = useQuery({
    queryKey: ['fiskal-order-documents', orderId],
    queryFn: () => fetchOrderDocuments(orderId),
    enabled: expanded && orderId > 0,
    staleTime: 60_000,
  })

  /**
   * §9.333 - whether this order cannot be closed until somebody owns it.
   *
   * The channels are read rather than guessed: `paymentNeedsWaiter` used to hardcode
   * `restaurant || bar`, which missed `na_sekeru` - the owner's own channel and the one that was
   * stuck. Cached hard: a franchise's channel list does not change while an archive page is open.
   */
  const { data: channelData } = useGetSalesChannels({ query: { staleTime: 30 * 60_000 } })
  const channels = useMemo(() => (Array.isArray(channelData) ? channelData : []), [channelData])
  const needsWaiter = needsWaiterBeforeClosing(order, channels)
  const [waiterId, setWaiterId] = useState<number | null>(null)
  const { data: waiterData, isSuccess: waitersLoaded } = useGetEmployeesWaiters({
    // Only asked for when the row is actually offering the picker.
    query: { enabled: expanded && needsWaiter },
  })
  const waiters = useMemo(() => waiterOptions(waiterData), [waiterData])
  const assignWaiter = usePatchOrdersOrderId()

  const transitions = availableTransitions(order.status, {
    canChangeStatus: hasPermission('change_order_status'),
    canCancel: hasPermission('cancel_order'),
  })
  const showReturnToKitchen = canReturnToKitchen({
    systemRole: user?.systemRole,
    hasPermission,
    status: order.status,
  })
  // Legacy spells this `… || isMainAdmin`. It does not need to: `hasPermission`
  // already answers true for a main_admin without consulting the matrix.
  const canStorno = hasPermission('fiskal') || hasPermission('fiskal_storno')

  const [printing, setPrinting] = useState(false)

  /**
   * Issue the fiscal document this order never got - §9.645, owner O36.
   *
   * **No new endpoint.** `postFiskalReceipt` is what the till calls and what the dispatch board loops over, and
   * this row already holds everything its payload needs.
   *
   * **The stored payment method is used rather than asked for.** The order was settled; which way is a recorded
   * fact, and asking again on a delivered order invites somebody to answer differently from the money that was
   * actually taken - §9.579 is the entry where a dialog opening on a value nobody chose was the defect.
   *
   * **`ALREADY_PRINTED` is reported as the document being out, not as a failure.** §9.362 settled that: the
   * server answers 409 with the document's number in the sentence and there is nothing for the operator to do
   * except know it exists. `alreadyPrinted` and `alreadyPrintedNote` are the shared readers, so two screens
   * cannot report one 409 differently.
   *
   * **A virtual receipt is opened for printing.** §9.498 is the entry where the dispatch board threw that body
   * away and silently printed nothing on 2 237 of the owner's documents; `virtualReceiptOf` is the shared reader
   * that stops this repeating it.
   */
  async function issueFiskalDocument() {
    setPrinting(true)
    try {
      const answer = await postFiskalReceipt({
        orderId: order.id ?? 0,
        orderNumber: order.orderNumber ?? '',
        orderType: order.type ?? undefined,
        paymentMethod: order.paymentMethod ?? 'cash',
        total: parseMoneyToCents(order.total) / 100,
        items: receiptLines(
          (order.items ?? []).map((line) => ({
            productName: line.productName ?? undefined,
            quantity: line.quantity ?? 0,
            unitPrice: line.unitPrice ?? 0,
          })),
          order.type ?? undefined,
          channels.find((one) => (one as { slug?: string }).slug === order.type),
        ),
      })
      const virtual = virtualReceiptOf(answer)
      if (virtual !== null && !printVirtualReceipt(virtual, Date.now())) {
        toast({
          variant: 'destructive',
          title: tt('Doklad sa nepodarilo otvoriť'),
          description: tt('Povoľte vyskakovacie okná a doklad vytlačte znova.'),
        })
      }
      toast({ title: tt('Fiškálny doklad vytlačený') })
      onRefresh()
    } catch (caught) {
      if (alreadyPrinted(caught)) {
        toast({ title: tt('Doklad už bol vytlačený'), description: alreadyPrintedNote(caught) })
        onRefresh()
        return
      }
      toast({
        variant: 'destructive',
        title: tt('Doklad sa nepodarilo vytlačiť'),
        description: apiErrorMessage(caught, ''),
      })
    } finally {
      setPrinting(false)
    }
  }

  /* §9.645, owner O36 - the pair is a named rule in `fiskal-row-actions.ts`, because the behaviour is that the
     two are complements: a component can be read to see which buttons exist, and only a rule can be asked
     whether they are mutually exclusive on every input. */
  const { showStorno: showStornoButton, showPrint: showPrintButton } = fiskalRowActions(
    order,
    canStorno,
  )

  const TypeIcon = TYPE_ICONS[order.type ?? ''] ?? Store
  const itemsSummary =
    order.items?.map((line) => `${line.quantity ?? 0}× ${line.productName ?? ''}`).join(', ') || '-'

  return (
    <>
      {/* §9.467 - the row names the order it is. Every other list in this app has a row marker and this
        * one had none, so a spec could only find a row by the text inside it - which on an archive of
        * 10 000 rows is a substring match against every customer name and note on the page. */}
      <tr
        data-order-row={String(order.id ?? '')}
        className={cn(
          'border-b border-border hover:bg-muted/20 transition-colors cursor-pointer select-none',
          expanded && 'bg-muted/10',
        )}
        onClick={() => setExpanded(!expanded)}
      >
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-2">
            {expanded ? (
              <ChevronUp className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            ) : (
              <ChevronDown className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
            )}
            <div>
              <div className="flex items-center gap-1.5">
                <span className="font-mono text-xs font-bold text-primary">
                  #{order.orderNumber}
                </span>
                <SourceBadge
                  source={order.source}
                  channel={channels.find(
                    (one) => (one as { slug?: string }).slug === order.type,
                  )}
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-0.5">
                {order.createdAt
                  ? new Date(order.createdAt).toLocaleString('sk-SK', {
                      day: '2-digit',
                      month: '2-digit',
                      hour: '2-digit',
                      minute: '2-digit',
                    })
                  : '-'}
              </p>
            </div>
          </div>
        </td>
        <td className="px-4 py-3.5">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground font-medium">
            <TypeIcon className="w-3.5 h-3.5" />
            {ORDER_TYPE_LABELS[order.type ?? ''] ?? order.type}
          </span>
        </td>
        <td className="px-4 py-3.5">
          {order.customerName ? (
            <span className="text-sm font-semibold text-foreground">{order.customerName}</span>
          ) : (
            <span className="text-xs text-muted-foreground italic">-</span>
          )}
          {order.customerPhone && (
            <div className="text-[10px] text-muted-foreground">{order.customerPhone}</div>
          )}
          {/* §9.138 - the archive is read by scanning it for *which order went to Hlavná 12*, and an
           * address that costs a click per row cannot be scanned. Truncated because one long
           * address must not make the whole table ragged; the expanded row keeps it whole. */}
          {order.customerAddress && (
            <div
              data-row-address
              title={order.customerAddress}
              className="flex items-center gap-1 text-[10px] text-muted-foreground truncate max-w-[190px]"
            >
              <MapPin className="w-3 h-3 flex-shrink-0" />
              {order.customerAddress}
            </div>
          )}
        </td>
        <td className="px-4 py-3.5">
          <div className="text-[10px] text-muted-foreground truncate max-w-[160px]">
            {itemsSummary}
          </div>
        </td>
        <td className="px-4 py-3.5 text-right">
          <span className="font-bold text-foreground text-sm">{formatMoney(order.total)}</span>
          <p className="text-[10px] text-muted-foreground">
            {paymentMethodLabel(order.paymentMethod)}
          </p>
          {/* §9.210 - "on every order", and the row somebody scans is where *every* lives. The
           * expanded bill carries the amount and the operator; this is the fact of it. */}
          {(order.discount ?? 0) > 0 && (
            <p
              className="text-[10px] font-medium text-emerald-600"
              data-discount-badge={discountSource(order).kind}
            >
              {discountSource(order).label}
            </p>
          )}
        </td>
        <td className="px-4 py-3.5">
          <div className="flex items-center gap-2">
            <StatusPill status={order.status} />
            <EditedFlag order={order} />
            {/* §9.578, FB-880 - "so we can check if it was printed in different kasa". Gated on the
             * order having a document, because an order nobody has rung up has no register to name
             * and a blank badge in every row is worse than none. */}
            <KasaFlag order={order} />
            {/* §9.573, FB-866 - the owner's second exclamation, behind `fiskal_reconcile`. */}
            <FiskalMismatchFlag order={order} shown={maySeeFiskalMismatch} />
          </div>
        </td>
      </tr>

      {expanded && (
        <tr className="border-b border-border bg-muted/5">
          <td colSpan={6} className="px-6 py-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  <T>Detaily objednávky</T>
                </p>
                {order.customerAddress && (
                  <div className="flex items-start gap-2 text-xs text-muted-foreground">
                    <MapPin className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" /> {order.customerAddress}
                  </div>
                )}
                {order.notes && (
                  <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 text-xs text-amber-600">
                    📝 {order.notes}
                  </div>
                )}
                <div className="text-[10px] text-muted-foreground space-y-0.5">
                  <p>
                    <T>Vytvorená:</T> {formatDateTime(order.createdAt)}
                  </p>
                  {order.updatedAt !== order.createdAt && (
                    <p>
                      <T>Aktualizovaná:</T> {formatDateTime(order.updatedAt)}
                    </p>
                  )}
                  {order.employeeName && (
                    <p>
                      <T>Čašník:</T> {order.employeeName}
                    </p>
                  )}
                  {order.driverName && (
                    <p>
                      <T>Šofér:</T> {order.driverName}
                    </p>
                  )}
                  {order.isEdited && order.updatedByName && (
                    <p className="text-red-500 font-medium">
                      <T>Upravil:</T> {order.updatedByName}
                    </p>
                  )}
                  {order.updatedByName && !order.isEdited && (
                    <p
                      className={
                        order.status === 'cancelled'
                          ? 'text-red-500 font-medium'
                          : 'text-sky-500 font-medium'
                      }
                    >
                      {order.status === 'cancelled' ? tt('Zrušil') : tt('Zmenil stav')}:{' '}
                      {order.updatedByName}
                    </p>
                  )}
                  {order.confirmedByName && (
                    <p className="text-violet-500 font-medium">
                      <T>Overil:</T> {order.confirmedByName}
                    </p>
                  )}
                  {order.changeReason && (
                    <p className="text-muted-foreground">
                      <T>Dôvod:</T> {order.changeReason}
                    </p>
                  )}
                </div>
                {/* §9.787, owner O140 - a bad order's phone, e-mail or address, blocked from the order itself. */}
                {mayBlock && <OrderBlockAction order={order} />}
              </div>

              <div className="space-y-2">
                <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                  <T>Položky</T>
                </p>
                <div className="space-y-1">
                  {order.items?.map((line) => (
                    <div
                      key={line.id}
                      className="flex justify-between text-xs bg-muted/50 rounded-lg px-3 py-1.5"
                    >
                      <span className="font-medium text-foreground">
                        <span className="text-primary font-bold">{line.quantity}×</span>{' '}
                        <T>{line.productName ?? ''}</T>
                      </span>
                      <span className="flex items-center gap-2">
                        {/* §9.5's DPH bullet. Drawn only where the order mixes rates - food at 5 %
                            beside a drink at 19 % - because repeating one rate down every line of a
                            single-rate order is noise, and single-rate is still almost all of them.
                            The line's own snapshot, never the product's: the two disagree on 77 % of
                            stored lines, so reading the product would restate three orders in four. */}
                        {!oneRate && lineVatLabel(line) !== '' && (
                          <span className="text-[10px] text-muted-foreground" data-line-vat>
                            {lineVatLabel(line)}
                          </span>
                        )}
                        <span className="font-bold text-foreground">
                          {formatCents(lineTotalCents(line.unitPrice, line.quantity ?? 0))}
                        </span>
                      </span>
                    </div>
                  ))}
                  {/* §9.210 - which discount, not only that there was one. */}
                  {(order.discount ?? 0) > 0 &&
                    (() => {
                      const source = discountSource(order)
                      return (
                        <div
                          className="flex justify-between text-xs px-3 py-1 text-emerald-600"
                          data-discount-kind={source.kind}
                        >
                          <span>
                            <T>Zľava</T>
                            {source.label !== '' && (
                              <span
                                className={cn(
                                  'ml-1',
                                  source.kind === 'unrecorded'
                                    ? 'text-muted-foreground italic'
                                    : 'text-foreground font-medium',
                                )}
                                data-discount-source
                              >
                                · {source.label}
                              </span>
                            )}
                            {source.operator !== null && (
                              <span className="ml-1 text-muted-foreground" data-discount-operator>
                                ({source.operator})
                              </span>
                            )}
                          </span>
                          <span>−{formatMoney(order.discount)}</span>
                        </div>
                      )
                    })()}
                  {(order.deliveryCost ?? 0) > 0 && (
                    <div className="flex justify-between text-xs px-3 py-1 text-muted-foreground">
                      <span>
                        <T>Dovoz</T>
                      </span>
                      <span>+{formatMoney(order.deliveryCost)}</span>
                    </div>
                  )}
                  <div className="flex justify-between text-sm font-bold px-3 pt-1 border-t border-border">
                    <span>
                      <T>Spolu</T>
                    </span>
                    <span className="text-primary">{formatMoney(order.total)}</span>
                  </div>
                  {/* Stated once when every line shares a rate, which on the restored data is every
                      order: A is dine-in at 5 %, B is delivery and drinks at 19 %, so the rate
                      follows the channel and a bill does not mix them. Drawing the tax only on a
                      mixed-rate order would have drawn it on none of the 10 785, which is not what
                      §9.5 asks for. */}
                  {orderVat !== '' && (
                    <div className="flex justify-between text-[11px] px-3 text-muted-foreground">
                      <span>
                        <T>DPH</T>
                      </span>
                      <span data-order-vat>{orderVat}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* §9.333 - the owner: *"its in zoznam & archív as kontrola kvality, but we cant move
                  it."* The transition button below is offered and the server refuses it, because
                  `guardClosable` will not close an order on a table channel without a waiter - and
                  `employeeId` was settable nowhere this row can reach. Shown only when all three of
                  the server's own conditions hold, so a row where the question does not arise shows
                  nothing. */}
              {needsWaiter && (
                <div className="space-y-2" data-needs-waiter={orderId}>
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Čašník</T>
                  </p>
                  <p className="text-[11px] text-amber-500">
                    <T>
                      Bez priradeného čašníka sa objednávka nedá uzatvoriť - niekto musí mať tržbu
                      na sebe.
                    </T>
                  </p>
                  <div className="flex gap-2">
                    <select
                      data-assign-waiter={orderId}
                      aria-label={tt('Priradiť čašníka')}
                      value={waiterId ?? ''}
                      onChange={(event) =>
                        setWaiterId(event.target.value === '' ? null : Number(event.target.value))
                      }
                      className="flex-1 min-h-[36px] rounded-xl border border-border bg-background px-3 text-xs"
                    >
                      <option value="">{tt('- vyberte čašníka -')}</option>
                      {waiters.map((waiter) => (
                        <option key={waiter.id} value={waiter.id}>
                          {waiter.name}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      data-assign-waiter-save={orderId}
                      disabled={waiterId === null || assignWaiter.isPending}
                      onClick={(event) => {
                        event.stopPropagation()
                        void assignWaiter
                          .mutateAsync({ orderId, data: { employeeId: waiterId } })
                          .then(() => onRefresh())
                      }}
                      className="min-h-[36px] px-3 rounded-xl border border-primary/40 bg-primary/10 text-primary text-xs font-bold disabled:opacity-40"
                    >
                      <T>Priradiť</T>
                    </button>
                  </div>
                  {waitersLoaded && waiters.length === 0 && (
                    <p className="text-[11px] text-muted-foreground" data-no-waiters>
                      <T>Žiadni čašníci s dochádzkou - pridajte dochádzku.</T>
                    </p>
                  )}
                </div>
              )}

              {transitions.length > 0 && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Zmena stavu</T>
                  </p>
                  <div className="space-y-2">
                    {transitions.map((next) => {
                      const Icon = TRANSITION_ICONS[next]
                      return (
                        <button
                          key={next}
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation()
                            onStatusRequest(order, next)
                          }}
                          className={cn(
                            'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border transition-all',
                            TRANSITION_BUTTON_CLASSES[next] ??
                              'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20',
                          )}
                        >
                          <span className="flex items-center gap-1.5">
                            {Icon && <Icon className="w-3.5 h-3.5" />}
                            {orderStatusLabel(next)}
                          </span>
                          {PROTECTED_STATUSES.includes(next) && <StepUpHint />}
                        </button>
                      )
                    })}
                  </div>
                </div>
              )}

              {transitions.length === 0 && !isMainAdmin && !showReturnToKitchen && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Stav</T>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <T>Táto objednávka je ukončená.</T>
                  </p>
                </div>
              )}

              {showReturnToKitchen && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Vrátenie do kuchyne</T>
                  </p>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      onReturnToKitchen(order)
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border bg-amber-500/10 text-amber-500 border-amber-500/20 hover:bg-amber-500/20 transition-all"
                  >
                    <span className="flex items-center gap-1.5">
                      <RotateCcw className="w-3.5 h-3.5" /> <T>Vrátiť do kuchyne</T>
                    </span>
                    <StepUpHint />
                  </button>
                  <p className="text-[10px] text-muted-foreground leading-snug">
                    <T>
                      Objednávka sa znovu objaví v kuchyni a odráta sa šoférovi, ktorý ju doručil.
                      Po opätovnom doručení sa pripíše šoférovi, ktorý ju doručí.
                    </T>
                  </p>
                </div>
              )}

              {showPrintButton && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Fiškálny doklad</T>
                  </p>
                  <button
                    type="button"
                    data-print-fiskal
                    disabled={printing}
                    onClick={(event) => {
                      event.stopPropagation()
                      void issueFiskalDocument()
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border bg-emerald-500/10 text-emerald-500 border-emerald-500/20 hover:bg-emerald-500/20 transition-all disabled:opacity-50"
                  >
                    <span className="flex items-center gap-1.5">
                      <Printer className="w-3.5 h-3.5" />{' '}
                      {order.fiskalStornoAt ? (
                        <T>Vytlačiť nový fiškálny doklad</T>
                      ) : (
                        <T>Vytlačiť fiškálny doklad</T>
                      )}
                    </span>
                  </button>
                </div>
              )}

              {showStornoButton && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Fiškálny doklad</T>
                  </p>
                  <button
                    type="button"
                    onClick={(event) => {
                      event.stopPropagation()
                      setStornoTarget(order)
                    }}
                    className="w-full flex items-center justify-between gap-2 px-3 py-2 rounded-xl text-xs font-bold border bg-red-500/10 text-red-500 border-red-500/20 hover:bg-red-500/20 transition-all"
                  >
                    <span className="flex items-center gap-1.5">
                      <Ban className="w-3.5 h-3.5" /> <T>Storno fiškálneho dokladu</T>
                    </span>
                    <StepUpHint />
                  </button>
                </div>
              )}

              {order.fiskalStornoAt && (
                <div className="space-y-1">
                  <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                    <T>Fiškálny doklad</T>
                  </p>
                  <div className="px-3 py-2 rounded-xl border border-red-500/20 bg-red-500/5 text-xs text-red-400">
                    <div className="flex items-center gap-1.5 font-bold">
                      <Ban className="w-3.5 h-3.5" /> <T>Stornovaný</T>
                    </div>
                    <div className="text-[10px] text-muted-foreground mt-0.5">
                      {formatDateTime(order.fiskalStornoAt)}
                      {order.fiskalStornoBy ? ` · ${order.fiskalStornoBy}` : ''}
                    </div>
                  </div>
                </div>
              )}

              {isMainAdmin && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" /> <T>Zmeniť stav (admin)</T>
                  </p>
                  <div className="flex gap-2">
                    <select
                      aria-label={tt('Zmeniť stav (admin)')}
                      value={forcedStatus}
                      onChange={(event) => setForcedStatus(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      className={SELECT_CLASSES}
                    >
                      <option value="" disabled>
                        {tt('- vybrať stav -')}
                      </option>
                      {ALL_STATUSES.filter((status) => status !== order.status).map((status) => (
                        <option key={status} value={status}>
                          {orderStatusLabel(status)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      onClick={(event) => {
                        event.stopPropagation()
                        if (forcedStatus) onStatusRequest(order, forcedStatus, true)
                      }}
                      className={ADMIN_BUTTON_CLASSES}
                    >
                      <Star className="w-3 h-3" /> <T>Potvrdiť</T>
                    </button>
                  </div>
                </div>
              )}

              {isMainAdmin && (
                <div className="space-y-2">
                  <p className="text-xs font-bold text-amber-500 uppercase tracking-wider flex items-center gap-1.5">
                    <Star className="w-3.5 h-3.5" /> <T>Zmeniť spôsob platby (admin)</T>
                  </p>
                  <p className="text-[11px] text-muted-foreground">
                    <T>Aktuálne:</T>{' '}
                    <span className="font-semibold text-foreground">
                      {paymentMethodLabel(order.paymentMethod)}
                    </span>
                  </p>
                  <div className="flex gap-2">
                    <select
                      // §9.467 - `TODO.md` item 3b: nothing had ever driven this control, so it carried
                      // no marker. Both it and the button below need one, because the page has two
                      // *Potvrdiť* buttons once the step-up prompt is open.
                      data-payment-method-select
                      aria-label={tt('Zmeniť spôsob platby (admin)')}
                      value={forcedPaymentMethod}
                      onChange={(event) => setForcedPaymentMethod(event.target.value)}
                      onClick={(event) => event.stopPropagation()}
                      className={SELECT_CLASSES}
                    >
                      <option value="" disabled>
                        {tt('- vybrať spôsob platby -')}
                      </option>
                      {/* §9.772, owner O132 - five named choices, QR Pay among them, each saying which card it is.
                        * It listed every label but QR Pay (§9.741), legacy's unused `card` included. */}
                      {ARCHIVE_PAYMENT_CHOICES.filter((method) => method !== order.paymentMethod).map((method) => (
                        <option key={method} value={method}>
                          {paymentChoiceLabel(method)}
                        </option>
                      ))}
                    </select>
                    <button
                      type="button"
                      data-payment-method-confirm
                      onClick={(event) => {
                        event.stopPropagation()
                        if (forcedPaymentMethod) onPaymentMethodRequest(order, forcedPaymentMethod)
                      }}
                      className={ADMIN_BUTTON_CLASSES}
                    >
                      <Star className="w-3 h-3" /> <T>Potvrdiť</T>
                    </button>
                  </div>
                  {forcedPaymentMethod !== '' && (
                    <p className="text-[11px] text-muted-foreground" data-payment-method-note={forcedPaymentMethod}>
                      {paymentChoiceNote(forcedPaymentMethod)}
                    </p>
                  )}
                </div>
              )}
            </div>

            <EditHistory
              rows={mergedHistory(
                (editLogs ?? []) as EditLogEntry[],
                statusLogs ?? [],
                fiskalDocumentEvents(fiskalDocumentsOf(fiskalDocuments)),
              )}
            />
          </td>
        </tr>
      )}

      {stornoTarget && (
        <EmployeeAuthModal
          meta={{
            orderId: stornoTarget.id ?? 0,
            orderNumber: stornoTarget.orderNumber ?? '',
            orderTotalCents: parseMoneyToCents(stornoTarget.total),
            newStatus: FISKAL_STORNO,
          }}
          onClose={() => setStornoTarget(null)}
          onSuccess={(_confirmedBy, response) => {
            setStornoTarget(null)
            // `AuthSuccessPayload` is `object` since the order responses became records; this
            // handler wants the storno body, which is read by name further down.
            onStornoSuccess(response as Record<string, unknown> | undefined)
            onRefresh()
          }}
        />
      )}
    </>
  )
}

/**
 * Where the order came from - §9.462.
 *
 * **`channel` decides whether a staff-typed order was phoned or taken here.** `orders.source` is
 * `staff ? 'phone' : 'web'`, one expression, so every table and every bar tab reads `phone` and the
 * badge used to print *Telefón* beside *Typ: Reštaurácia*. The owner reported exactly that. Optional,
 * because the list may draw before `/sales-channels` answers and a slow read must not change what a
 * row says.
 */
export function SourceBadge({
  source,
  channel,
}: {
  source: string | undefined
  channel?: BadgeChannel
}) {
  const kind = sourceBadgeKind(source, channel)
  if (kind === 'web') {
    return (
      <span className="flex items-center gap-0.5 text-[9px] font-bold rounded px-1 py-0.5 leading-none bg-blue-500/20 text-blue-400 border border-blue-500/35">
        <Globe className="w-2 h-2" />
        {sourceBadgeLabel(kind)}
      </span>
    )
  }
  if (kind === 'phone') {
    return (
      <span className="flex items-center gap-0.5 text-[9px] font-bold rounded px-1 py-0.5 leading-none bg-orange-500/20 text-orange-400 border border-orange-500/35">
        <Phone className="w-2 h-2" />
        <T>Telefón</T>
      </span>
    )
  }
  if (kind === 'on-premise') {
    return (
      <span
        className="flex items-center gap-0.5 text-[9px] font-bold rounded px-1 py-0.5 leading-none bg-zinc-500/20 text-zinc-400 border border-zinc-500/35"
        data-source-on-premise
      >
        <Store className="w-2 h-2" />
        <T>Na mieste</T>
      </span>
    )
  }
  return null
}

export function StatusPill({ status }: { status: string | undefined }) {
  return (
    <span
      className={cn(
        'text-xs font-bold px-2.5 py-1 rounded-full border',
        STATUS_PILL_CLASSES[status ?? ''] ?? 'bg-muted text-muted-foreground',
      )}
    >
      {ORDER_STATUS_LABELS[status ?? ''] ?? status}
    </span>
  )
}

/**
 * §9.286, FB-443 — the badge that says an order was edited.
 *
 * **The glyph and the classes are legacy's and stay legacy's.** What is added is an accessible name:
 * the explanation was a `title` alone, and `title` is a hover tooltip — there is no hover on a
 * tablet, which is the device O1 is about, and none for a screen reader on any device. So on the
 * owner's own screen this badge said nothing at all.
 *
 * Deliberately **not** widened. `/lists` at a tablet width was one of O1's overflow findings
 * (§9.270); putting a word inside this pill is the one change that would re-open it.
 */
export function EditedFlag({ order }: { order: OrderListItem }) {
  if (!order.isEdited) return null
  const label = editedFlagLabel(order)
  return (
    <span
      className="text-xs font-black text-red-500 bg-red-500/15 border border-red-500/30 rounded-full px-1.5 py-0.5 leading-none"
      title={label}
      aria-label={label}
      data-edited-flag
    >
      !
    </span>
  )
}

/**
 * A finished order whose document and status disagree - §9.573, FB-866.
 *
 * **Absent rather than empty without the right.** An element that renders nothing beside the status reads
 * as *nothing is wrong here*, which is a claim - and this whole class of defect is about screens making
 * claims they cannot support. So the caller passes `shown` and this returns null.
 *
 * **Amber rather than the edit flag's red**, so the two are distinguishable at a glance: one says somebody
 * changed the order, this says the register and the order disagree about whether a sale happened.
 */
function FiskalMismatchFlag({
  order,
  shown,
}: {
  order: OrderListItem
  shown: boolean
}) {
  if (!shown) return null
  const mismatch = fiskalMismatch(order)
  if (mismatch === null) return null
  const note = fiskalMismatchNote(mismatch)
  return (
    <span
      className="text-xs font-black text-amber-600 bg-amber-500/15 border border-amber-500/30 rounded-full px-1.5 py-0.5 leading-none"
      title={note}
      aria-label={note}
      data-fiskal-mismatch={mismatch}
    >
      !
    </span>
  )
}

/**
 * Which register printed this order's receipt - §9.578, FB-880.
 *
 * **The owner's own words**: *"add which KASA was it printed on... so we can check if it was printed
 * in different kasa"*. §10A.249 is why that question is worth a badge - Nesvady's two registers share
 * one host **and one port** while Šurany's do not, so *which device answered* is what their fiscal
 * figures keep turning on.
 *
 * **`fiskalKasaId` has been on `OrderListItem` all along** and no screen read it; verified against the
 * running app rather than off the type, because a declared field is not evidence the value arrives.
 *
 * **Gated on a document existing, not on the id being present.** Those are the same thing today, and
 * saying which one the render depends on is what stops a later reader relaxing it: the badge is a fact
 * about a receipt, so an order with no print must not carry one.
 */
function KasaFlag({ order }: { order: OrderListItem }) {
  /* The franchise's own register names, not a map of the two the port ships with - the owner's
   * correction, and they are right: `POST /fiskal/registers` creates as many as they like, each with
   * a `label`. `useGetFiskalRegisters` is shared by key, so one request serves every row on the page,
   * and a failed or refused read (this endpoint wants `fiskal`/`fiskal_view` and the archive does
   * not) leaves `registerName` falling back to the shipped names and then to the id itself - which
   * is a badge that is still true rather than one that vanishes. */
  const { data: registerData } = useGetFiskalRegisters()
  if (!order.fiskalPrintedAt) return null
  const rows = Array.isArray(registerData)
    ? (registerData as Array<{ kasaId?: string; label?: string }>)
    : []
  const label = registerName(order.fiskalKasaId, rows, SHIPPED_REGISTER_NAMES)
  if (label === '') return null
  return (
    <span
      className="text-[10px] font-semibold text-muted-foreground bg-muted border border-border rounded-full px-1.5 py-0.5 leading-none"
      data-order-kasa={order.fiskalKasaId ?? ''}
    >
      {label}
    </span>
  )
}

/**
 * *História zmien* - both trails, in time order - §9.446, FB-763.
 *
 * **The owner asked for four kinds of change and this panel showed two.** The payment method was
 * never recorded (`BACKEND.md` §10A.208 records it now) and the status trail was recorded in a
 * different table with **no caller at all** - 20 690 rows naming who confirmed each change.
 *
 * `mergedHistory` does the interleaving, so what this component decides is only how each kind looks.
 */
function EditHistory({ rows }: { rows: HistoryRow[] }) {
  if (rows.length === 0) return null

  return (
    <div className="mt-4 pt-4 border-t border-border">
      <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> <T>História zmien</T> ({rows.length})
      </p>
      <div className="space-y-2" data-order-history={String(rows.length)}>
        {rows.map((row, position) => {
          /* §9.570 - three kinds now, named rather than an `else`. The union widened when the fiscal
             trail joined and `tsc` refused the old two-branch ternary, which is the guarantee worth
             keeping: a fourth trail is a compile error here rather than a row rendered as an edit. */
          if (row.kind === 'status') {
            return <StatusHistoryRow key={`status-${row.entry.id ?? position}`} entry={row.entry} />
          }
          if (row.kind === 'fiskal') {
            return (
              <FiskalHistoryRow
                key={`fiskal-${row.entry.kind}-${String(row.entry.document.id)}-${String(position)}`}
                event={row.entry}
              />
            )
          }
          return <EditHistoryRow key={`edit-${row.entry.id ?? position}`} entry={row.entry} />
        })}
      </div>
    </div>
  )
}

/**
 * One fiscal document printed, or cancelled - §9.570, FB-864.
 *
 * **Its own shape rather than a fake edit entry**, for the reason `StatusHistoryRow` states one
 * paragraph down: a document has a receipt number, the figure the register was told to print and - on a
 * cancellation - who authorised it, none of which an edit-log `field -> {from, to}` has room for.
 *
 * **The figure is the point.** It is what the register printed, which is exactly what §9.463 compares
 * against the order's own total and what §10A.292 now refuses an edit from moving.
 */
function FiskalHistoryRow({ event }: { event: FiskalDocumentEvent }) {
  /* §9.578, FB-880 - this is the half that answers the question as the owner asked it. An order can
   * carry two documents (§10A.185 reissues after a storno) and they can name two registers, and this
   * row drew the receipt number, the figure, *virtuálny* and the storno author while leaving out the
   * one field that tells them apart. `kasaId` has been parsed since §9.569 and rendered nowhere. */
  const { data: registerData } = useGetFiskalRegisters()
  const registerRows = Array.isArray(registerData)
    ? (registerData as Array<{ kasaId?: string; label?: string }>)
    : []
  const register = registerName(event.document.kasaId, registerRows, SHIPPED_REGISTER_NAMES)
  const cancelled = event.kind === 'stornoed'
  return (
    <div
      className={
        cancelled
          ? 'bg-red-500/5 border border-red-500/10 rounded-xl px-3 py-2 text-xs'
          : 'bg-emerald-500/5 border border-emerald-500/10 rounded-xl px-3 py-2 text-xs'
      }
      data-fiskal-event={event.kind}
    >
      <div className="flex items-center justify-between mb-1">
        <span className={`font-bold flex items-center gap-1 ${cancelled ? 'text-red-600' : 'text-emerald-600'}`}>
          <Receipt className="w-3 h-3" />
          {cancelled ? <T>Fiškálny doklad stornovaný</T> : <T>Fiškálny doklad vytlačený</T>}
        </span>
        <span className="text-muted-foreground">{formatDateTime(event.at)}</span>
      </div>
      <div className="text-muted-foreground">
        {event.document.receiptNumber !== null && (
          <span className="font-semibold text-foreground">{event.document.receiptNumber}</span>
        )}
        {register !== '' && (
          <span className="font-semibold text-foreground" data-fiskal-event-kasa={event.document.kasaId ?? ''}>
            {event.document.receiptNumber !== null ? ' · ' : ''}
            {register}
          </span>
        )}
        {event.document.totalCents !== null && (
          <span>{` · ${formatCents(event.document.totalCents)}`}</span>
        )}
        {event.document.virtual && (
          <span>
            {' · '}
            <T>virtuálny</T>
          </span>
        )}
        {cancelled && event.document.stornoBy !== null && <span>{` · ${event.document.stornoBy}`}</span>}
      </div>
    </div>
  )
}

/**
 * One status change: who moved it, from what to what, and why if they said.
 *
 * **Its own shape rather than a fake `field → {from, to}`.** A status change has a reason and a
 * confirming employee, which an edit-log entry has no room for, and forcing it into that map would
 * mean inventing a field name and special-casing it straight back out.
 */
function StatusHistoryRow({ entry }: { entry: OrderStatusLog }) {
  return (
    <div
      className="bg-cyan-500/5 border border-cyan-500/10 rounded-xl px-3 py-2 text-xs"
      data-status-change={String(entry.id ?? 0)}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-cyan-600 flex items-center gap-1">
          <RotateCcw className="w-3 h-3" />
          {entry.confirmedByName ?? tt('Neznámy')}{' '}
          <span className="font-normal text-muted-foreground">({entry.confirmedByRole})</span>
        </span>
        <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
      </div>
      <div className="text-muted-foreground">
        <span className="font-semibold text-foreground">
          <T>Stav</T>:
        </span>{' '}
        <span className="line-through text-red-400">{orderStatusLabel(entry.fromStatus)}</span>
        {' → '}
        <span className="text-emerald-500">{orderStatusLabel(entry.toStatus)}</span>
        {entry.reason ? <span className="ml-1">({entry.reason})</span> : null}
      </div>
    </div>
  )
}

function EditHistoryRow({ entry }: { entry: EditLogEntry }) {
  /* §9.458 - slug to name for a channel change. Loaded here rather than threaded from `OrderRow`
   * because this is the component that draws it, and the query is shared and cached for half an
   * hour by the call above - the same `staleTime` for the same reason. */
  const { data: channelData } = useGetSalesChannels({ query: { staleTime: 30 * 60_000 } })
  const channelNames = useMemo(
    () =>
      Object.fromEntries(
        (Array.isArray(channelData) ? channelData : [])
          .map((one) => one as { slug?: string; name?: string })
          .filter((one) => typeof one.slug === 'string' && typeof one.name === 'string')
          .map((one) => [one.slug as string, one.name as string]),
      ),
    [channelData],
  )

  return (
    <div
      className="bg-red-500/5 border border-red-500/10 rounded-xl px-3 py-2 text-xs"
      data-edit-change={String(entry.id ?? 0)}
    >
      <div className="flex items-center justify-between mb-1">
        <span className="font-bold text-red-500 flex items-center gap-1">
          <Edit2 className="w-3 h-3" />
          {entry.employeeName ?? tt('Neznámy')}{' '}
          <span className="font-normal text-muted-foreground">({entry.employeeRole})</span>
        </span>
        <span className="text-muted-foreground">{formatDateTime(entry.createdAt)}</span>
      </div>
      <div className="space-y-0.5 text-muted-foreground">
        {Object.entries(entry.changes ?? {}).map(([field, change]) => {
          const described = describeEditLogChange(field, change, channelNames)
          if (described.kind === 'lines') {
            return (
              <div key={field}>
                <span className="font-semibold text-foreground">
                  <T>Položky zmenené:</T>
                </span>
                <div className="ml-2 mt-0.5">
                  <div className="text-red-400 line-through">{described.before}</div>
                  <div className="text-emerald-500">{described.after}</div>
                </div>
              </div>
            )
          }
          if (described.kind === 'added') {
            return (
              <div key={field}>
                <span className="font-semibold text-foreground">
                  <T>Pridané položky:</T>
                </span>
                <div className="ml-2 mt-0.5 text-emerald-500">{described.added}</div>
              </div>
            )
          }
          return (
            <div key={field}>
              <span className="font-semibold text-foreground">{editLogFieldLabel(field)}:</span>{' '}
              <span className="line-through text-red-400">{described.before}</span>
              {' → '}
              <span className="text-emerald-500">{described.after}</span>
            </div>
          )
        })}
      </div>
    </div>
  )
}
